import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Crown, Gavel, Plane, Users } from "lucide-react";

export default function AdminClub() {
  const [memberships, setMemberships] = useState([]);
  const [requests, setRequests] = useState({ rsvps: [], trip_requests: [], bid_requests: [] });
  const [tab, setTab] = useState("memberships");

  useEffect(() => {
    api.get("/club/admin/memberships").then((r) => setMemberships(r.data || [])).catch(() => {});
    api.get("/club/admin/requests").then((r) => setRequests(r.data || { rsvps: [], trip_requests: [], bid_requests: [] })).catch(() => {});
  }, []);

  const fmtDate = (iso) => {
    try { return iso ? new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" }) : "—"; } catch { return iso; }
  };

  const active = memberships.filter((m) => m.status === "active");
  const pending = memberships.filter((m) => m.status !== "active");

  const tabs = [
    { key: "memberships", label: `Iscrizioni (${active.length})`, icon: Crown },
    { key: "rsvps", label: `RSVP eventi (${requests.rsvps.length})`, icon: Users },
    { key: "trips", label: `Richieste viaggi (${requests.trip_requests.length})`, icon: Plane },
    { key: "bids", label: `Offerte aste (${requests.bid_requests.length})`, icon: Gavel },
  ];

  return (
    <div data-testid="admin-club-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold mb-2">Super Admin · Club Privilegia</div>
      <h1 className="editorial-heading text-4xl md:text-5xl mb-8">Gestione Club</h1>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[hsl(var(--border))]">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-club-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              tab === t.key ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "memberships" && (
        <div className="grid md:grid-cols-2 gap-4">
          {memberships.length === 0 && <div className="text-muted-foreground">Nessuna iscrizione registrata.</div>}
          {memberships.map((m) => (
            <div key={m.id} data-testid={`membership-${m.id}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4 rounded-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">{m.tier || m.pending_tier || "—"}</div>
                  <div className="font-semibold">{m.email}</div>
                  <div className="text-xs text-muted-foreground">Dal {fmtDate(m.started_at)} · fino al {fmtDate(m.expires_at)}</div>
                </div>
                <span className={`chip ${m.status === "active" ? "bg-green-900/30 text-green-300" : "bg-amber-900/30 text-amber-300"}`}>
                  {m.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "rsvps" && (
        <div className="space-y-2">
          {requests.rsvps.length === 0 && <div className="text-muted-foreground">Nessuna RSVP.</div>}
          {requests.rsvps.map((r) => (
            <div key={r.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-3 rounded-sm flex justify-between items-center">
              <div>
                <div className="font-medium">{r.email}</div>
                <div className="text-xs text-muted-foreground">Evento {r.event_id?.substring(0, 8)} · {r.guests} ospiti · {fmtDate(r.created_at)}</div>
              </div>
              <span className="chip bg-[hsl(var(--muted))]">{r.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "trips" && (
        <div className="space-y-2">
          {requests.trip_requests.length === 0 && <div className="text-muted-foreground">Nessuna richiesta viaggio.</div>}
          {requests.trip_requests.map((r) => (
            <div key={r.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-3 rounded-sm flex justify-between items-center">
              <div>
                <div className="font-medium">{r.email}</div>
                <div className="text-xs text-muted-foreground">Trip {r.trip_id?.substring(0, 8)} · {r.participants} pax · {fmtDate(r.created_at)}</div>
              </div>
              <span className="chip bg-[hsl(var(--muted))]">{r.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "bids" && (
        <div className="space-y-2">
          {requests.bid_requests.length === 0 && <div className="text-muted-foreground">Nessuna offerta registrata.</div>}
          {requests.bid_requests.map((r) => (
            <div key={r.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-3 rounded-sm flex justify-between items-center">
              <div>
                <div className="font-medium">{r.email}</div>
                <div className="text-xs text-muted-foreground">Asta {r.auction_id?.substring(0, 8)} · {fmtDate(r.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-[hsl(var(--accent))]">€{Number(r.max_bid).toLocaleString("it-IT")}</div>
                <span className="chip bg-[hsl(var(--muted))]">{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
